/**
 * @file 插件钱包赎回模块
 */
import { Button } from '@bfe/moly';
import { ShowType } from '@/types/account';
import {
  useUnstakeAmount,
  useSelectCoinList,
  useSlippage,
  useSolLoss,
} from '@/containers/operation/account/unstakeInfo/hooks';
import {
  useGasFee,
  useButtonStatus,
  useUnstake,
  useUnstakeStage,
} from './hooks';
import { useAccountInfoContext } from '@/hooks/useAccountInfo';
import { GasCircleRow } from '@/components';
import {
  UnstakeProcess,
  InputArea,
  SelectUnstakeType,
  DelayPeriod,
  DelayRedeemNotify,
} from '@/containers/operation/account/unstakeInfo/components';
import { Process } from '@/types/unstake';
import { useFm } from '@web3/base-hooks';
import styles from './index.module.scss';
import { ConfirmModal } from '../components/unstakeProcess/confirm';
import { useEffect } from 'react';
interface Props {
  onChangeShowType: (v: ShowType) => void;
  showType: ShowType;
}

export const ThirdUnstakeInfo = (props: Props) => {
  const { onChangeShowType, showType } = props;
  const fm = useFm();
  const { accountInfo } = useAccountInfoContext();
  const { unstakeStage, onChangeUnstakeStage } = useUnstakeStage();
  const { unstakeAmount, process, onChangeProcess, onChangeUnstakeAmount } =
    useUnstakeAmount({
      accountInfo,
    });

  const {
    selectCoinList,
    unstakeType,
    unstakeCoinAmountStore,
    delayRedeemInfo,
    onChangeUnstakeType,
    onChangeSelectCoinList,
  } = useSelectCoinList({
    unstakeAmount,
    showType,
  });

  const { gasStore } = useGasFee({
    unstakeCoinAmountStore,
    unstakeAmount,
    selectCoinList,
    unstakeType,
    process,
    showType,
    onChangeUnstakeStage,
  });

  const { slippage, slippageIsLost } = useSlippage(
    selectCoinList,
    unstakeCoinAmountStore
  );

  const { isSolLoss } = useSolLoss(selectCoinList, unstakeCoinAmountStore);

  const { orderCode, confirmButtonIsLoading, beginUnstake } = useUnstake({
    showType,
    unstakeCoinAmountStore,
    unstakeAmount,
    slippage,
    selectCoinList,
    process,
    unstakeStage,
    onChangeUnstakeStage,
    onChangeProcess,
  });

  const { buttonDisabled, buttonText, onChangeTextIsError, onClickUnstake } =
    useButtonStatus({
      showType,
      unstakeAmount,
      gasStore,
      slippageIsLost,
      unstakeStage,
      confirmButtonIsLoading,
      isSolLoss,
      onChangeProcess,
      onChangeShowType,
    });

  useEffect(() => {
    if (
      process === Process.Confirm &&
      (showType === ShowType.DelayUnStake || showType === ShowType.Unstake)
    ) {
      onChangeProcess(Process.None);
    }

    if (process === Process.Result) {
      onChangeShowType(ShowType.Result);
    }
  }, [showType, process]);

  return (
    <>
      {process !== Process.Result && (
        <>
          {(showType === ShowType.DelayUnStake ||
            showType === ShowType.Unstake) && (
            <>
              <InputArea
                unstakeAmount={unstakeAmount}
                gasStore={gasStore}
                onChangeTextIsError={onChangeTextIsError}
                onChangeAmount={onChangeUnstakeAmount}
              />
              <SelectUnstakeType
                isSolLoss={isSolLoss}
                unstakeType={unstakeType}
                selectCoinList={selectCoinList}
                unstakeCoinAmountStore={unstakeCoinAmountStore}
                onChangeUnstakeType={onChangeUnstakeType}
                onChangeSelectCoinList={onChangeSelectCoinList}
              />
              {/* 延迟赎回周期 */}
              <DelayPeriod
                show={showType === ShowType.DelayUnStake}
                delayRedeemInfo={delayRedeemInfo}
                showUpdateTime={true}
              />
              {/* 矿工费 */}
              <GasCircleRow
                show={!!unstakeAmount && gasStore.hasGasFee}
                gasStore={gasStore}
              />
              <div className={styles.btnContainer}>
                {/* 延迟赎回提示 */}
                <DelayRedeemNotify showType={showType} />
                {/* 解质押按钮 */}
                <Button
                  disabled={buttonDisabled}
                  loading={gasStore.isLoading && !gasStore.hasGasFee}
                  variant="primary"
                  size="large"
                  className={styles.button}
                  onClick={onClickUnstake}
                >
                  {showType === ShowType.DelayUnStake
                    ? fm('confirmDelayRedem')
                    : fm('remove_liquidity')}
                </Button>
              </div>
            </>
          )}
          {(showType === ShowType.ConfimDelayUnStake ||
            showType === ShowType.ConfirmUnstake) && (
            <ConfirmModal
              slippage={slippage}
              unstakeAmount={unstakeAmount}
              gasStore={gasStore}
              unstakeCoinAmountStore={unstakeCoinAmountStore}
              buttonIsLoading={confirmButtonIsLoading}
              selectCoinList={selectCoinList}
              slippageIsLost={slippageIsLost}
              confirmButtonText={buttonText}
              showType={showType}
              delayRedeemInfo={delayRedeemInfo}
              beginUnstake={beginUnstake}
            />
          )}
        </>
      )}

      {process !== Process.None && (
        <UnstakeProcess
          process={process}
          slippage={slippage}
          unstakeAmount={unstakeAmount}
          gasStore={gasStore}
          orderCode={orderCode}
          slippageIsLost={slippageIsLost}
          selectCoinList={selectCoinList}
          unstakeCoinAmountStore={unstakeCoinAmountStore}
          showType={showType}
          delayRedeemInfo={delayRedeemInfo}
          onChangeProcess={onChangeProcess}
          onChangeShowType={onChangeShowType}
        />
      )}
    </>
  );
};
